import CustomInput from '@/components/common/CustomInput';
import TinyMceEditor from '@/components/common/TinyMceEditor';
import { Button } from '@/components/ui/button';
import { cn, showToastMessage } from '@/utils';
import { reactIcons } from '@/utils/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import { useUploadFileMutation } from '@/store/api/commonApi';
import { useAddBannersMutation } from '@/store/api/contentManagementApi';
import { promotionMessageSchema } from '@/utils/validation';

export type PromotionMessageFormData = z.infer<typeof promotionMessageSchema>;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp'];

const AddPromotion = () => {
  const navigate = useNavigate();

  const [content, setContent] = useState('');
  const [previewName, setPreviewName] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  const [uploadFile] = useUploadFileMutation();
  const [addBanners, { isLoading }] = useAddBannersMutation();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    setError,
    formState: { errors },
  } = useForm<PromotionMessageFormData>({
    resolver: zodResolver(promotionMessageSchema),
    defaultValues: {
      heading: '',
      description: '',
      bannerImage: '',
    },
  });

  const resetFileState = () => {
    setUploadedImageUrl('');
    setPreviewName('');
    setValue('bannerImage', '', {
      shouldValidate: true,
      shouldDirty: true,
    });

    const input = document.getElementById('bannerImageInput') as HTMLInputElement | null;
    if (input) input.value = '';
  };

  const handleClose = () => {
    reset({
      heading: '',
      description: '',
      bannerImage: '',
    });
    setContent('');
    resetFileState();
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      showToastMessage('error', 'Only PNG, JPG, JPEG, and WEBP files are allowed');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      showToastMessage('error', 'File size must be less than or equal to 10 MB');
      e.target.value = '';
      return;
    }

    try {
      setIsUploading(true);
      clearErrors('bannerImage');

      const res = await uploadFile(file).unwrap();
      const imageUrl = res?.url;

      if (!imageUrl) {
        throw new Error('No URL returned from upload');
      }

      setUploadedImageUrl(imageUrl);
      setPreviewName(res.meta.filename);

      setValue('bannerImage', imageUrl, {
        shouldValidate: true,
        shouldDirty: true,
      });

      showToastMessage('success', 'Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      resetFileState();
      setError('bannerImage', {
        type: 'manual',
        message: 'Upload failed, please try again.',
      });
      showToastMessage('error', 'Upload failed, please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = async (data: PromotionMessageFormData) => {
    try {
      const payload = {
        name: data.heading,
        heading: data.heading,
        description: data.description,
        bannerImage: previewName,
        size: '1080x420',
        type: 'PromotionBanner',
        platform: 'WebBanner',
      };

      await addBanners(payload).unwrap();

      showToastMessage('success', 'Promotion banner created successfully!');
      handleClose();
      navigate(-1);
    } catch (error) {
      console.error('Create promotion error:', error);
      showToastMessage('error', 'Failed to create promotion banner');
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-5 flex items-center gap-5">
        <reactIcons.circleBack
          size={32}
          onClick={() => navigate(-1)}
          className="cursor-pointer text-white"
        />
        <h1 className="text-2xl font-semibold text-white">Add Promotion</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {/* <CustomInput
            control={control}
            name="name"
            type="text"
            label="Name *"
            placeholder="Enter banner name"
            inputClassName="pr-10 text-grayish-blue !border-green"
            labelClassName="font-medium text-black"
          /> */}

          <CustomInput
            control={control}
            name="heading"
            type="text"
            label="Heading"
            required
            placeholder="Enter code"
            inputClassName="pr-10 text-grayish-blue !border-green"
            labelClassName="font-medium text-white"
          />
        </div>

        <div className="w-full">
          <label className="text-sm font-medium text-white">
            Upload Promotion Image (PNG, JPG, WEBP)
            <span className="text-red-500">*</span>
          </label>

          <div className="mt-3 flex h-[260px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-green/40 bg-green/10 bg-white px-4">
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.webp"
              onChange={handleFileUpload}
              className="hidden"
              id="bannerImageInput"
            />

            {uploadedImageUrl ? (
              <div className="flex flex-col items-center gap-3">
                <img
                  src={uploadedImageUrl}
                  alt="Banner preview"
                  className="max-h-[150px] max-w-[220px] rounded-lg object-contain"
                />
                <p className="text-sm font-medium text-green">✓ {previewName}</p>

                <button
                  type="button"
                  onClick={resetFileState}
                  className="text-sm text-red-500 underline"
                >
                  Remove & Upload New
                </button>
              </div>
            ) : (
              <>
                <img src="/images/contentManagement/pic.svg" alt="Upload banner" />
                <p className="mt-3 text-center text-lg font-medium text-black">
                  {isUploading ? 'Uploading...' : 'Click to upload'}
                </p>
                <p className="mt-1 text-center text-sm text-gray-400">
                  PNG, JPG, WEBP up to <b>10MB</b>{' '}
                  <label
                    htmlFor="bannerImageInput"
                    className="cursor-pointer text-blue-500 underline"
                  >
                    Browse
                  </label>
                </p>

                <label
                  htmlFor="bannerImageInput"
                  className={cn(
                    'mt-4 cursor-pointer rounded-lg bg-green px-4 py-2 text-sm text-black hover:bg-green/80',
                    isUploading && 'pointer-events-none opacity-60'
                  )}
                >
                  {isUploading ? 'Uploading...' : 'Add Banner'}
                </label>
              </>
            )}
          </div>

          {errors.bannerImage && (
            <p className="mt-1 text-sm text-red-500">{errors.bannerImage.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <label className={cn('text-sm font-medium text-white')}>
            Description <span className="text-red-500">*</span>
          </label>

          <TinyMceEditor
            value={content}
            onChange={(value) => {
              setContent(value);
              setValue('description', value, {
                shouldValidate: true,
                shouldDirty: true,
              });
            }}
          />

          {errors.description && (
            <p className="text-sm text-red-500">{errors.description.message}</p>
          )}
        </div>

        <div className="mt-3 flex justify-end gap-4 border-t border-green3 pt-4">
          <Button type="button" variant="destructive" onClick={handleClose}>
            Cancel
          </Button>

          <Button type="submit" disabled={isLoading || isUploading}>
            {isLoading ? 'Creating...' : 'Create'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddPromotion;
